import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Wand2, Image as ImageIcon, Loader2, RefreshCw, CheckCircle2, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useQRStore } from '@/stores/qrStore';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export function ArtisticAITab() {
  const { currentQR, setDesignSettings, setLogoSettings } = useQRStore();
  const [prompt, setPrompt] = useState('');
  const [style, setStyle] = useState('cyberpunk');
  const [isGenerating, setIsGenerating] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const handleGenerate = async () => {
    if (!prompt) {
      toast.error('Please enter a prompt first');
      return;
    }

    setIsGenerating(true);
    setPreviewUrl(null);

    try {
      const { data, error } = await supabase.functions.invoke('artistic-ai-qr', {
        body: { 
          url: currentQR.contentData?.url || 'https://nexqr.online',
          prompt,
          style 
        }
      });

      if (error) throw error;

      setPreviewUrl(data.imageUrl);
      toast.success('AI QR Preview generated!');
    } catch (error: any) {
      console.error('AI Gen error:', error);
      toast.error('Failed to generate AI QR: ' + error.message);
    } finally {
      setIsGenerating(false);
    }
  };

  const applyAIImage = () => {
    if (!previewUrl) return;
    // Apply the AI-generated image as logo background
    setLogoSettings({ url: previewUrl, enabled: true, size: 100 });
    toast.success('Applied AI design to your QR code!');
  };

  return (
    <div className="space-y-6">
      <div className="p-4 bg-primary/5 rounded-xl border border-primary/10">
        <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
          <Wand2 className="h-4 w-4 text-primary" />
          AI Artistic Generator
        </h4>
        <p className="text-xs text-muted-foreground">
          Blend your QR code into a beautiful AI-generated image. Enter a description of what you want to see.
        </p>
      </div>

      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="prompt">Prompt</Label>
          <Input 
            id="prompt"
            placeholder="e.g. A lush tropical forest with waterfalls..." 
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Style</Label>
            <select 
              className="w-full h-10 px-3 rounded-md border bg-background text-sm"
              value={style}
              onChange={(e) => setStyle(e.target.value)}
            >
              <option value="cyberpunk">Cyberpunk</option>
              <option value="watercolor">Watercolor</option>
              <option value="oil-painting">Oil Painting</option>
              <option value="3d-render">3D Render</option>
              <option value="minimalist">Minimalist</option>
            </select>
          </div>
          <div className="flex items-end">
            <Button 
              className="w-full gap-2" 
              onClick={handleGenerate}
              disabled={isGenerating || !prompt}
            >
              {isGenerating ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
              Generate
            </Button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {previewUrl && (
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="space-y-4 pt-4 border-t"
          >
            <Label className="text-xs uppercase font-bold text-muted-foreground">Preview Result</Label>
            <div className="aspect-square rounded-xl overflow-hidden border shadow-inner bg-muted relative group">
              <img 
                src={previewUrl} 
                alt="AI Generated QR" 
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                <Button size="sm" variant="secondary" onClick={applyAIImage}>
                  Apply Design
                </Button>
              </div>
            </div>
            <p className="text-[10px] text-center text-muted-foreground italic">
              Note: This is a preview. High-res generation may take longer.
            </p>
          </motion.div>
        )}
      </AnimatePresence>

      {!previewUrl && !isGenerating && (
        <div className="aspect-square rounded-xl border-2 border-dashed flex flex-col items-center justify-center text-muted-foreground bg-muted/20">
          <ImageIcon className="h-12 w-12 mb-4 opacity-20" />
          <p className="text-sm">Enter a prompt to see the magic</p>
        </div>
      )}
    </div>
  );
}
